/* eslint-disable no-nested-ternary */
import { ScopedCssBaseline, styled } from "@mui/material";
import { Content } from "@volt_developers/react-ui-components";
import { ReactNode, Suspense, useMemo } from "react";
import { Redirect } from "react-router-dom";
import { SentryRoute } from "sentry";

import { authRoutes } from "modules/auth";

import Can from "../components/can";
import Loader from "../components/Loader";
import { Page403 } from "../pages/Page403";
import appRoutes from "../routes";
import OldTheme from "../theme/OldTheme";
import { useAbility } from "./useAbility";

import type { AppAbilityType } from "../context/ability/ability";
import type { Action } from "../models/role/action";
import type { Subject } from "../models/role/subject";
import type { Route as RouteModel } from "../routes/RouteModel";

const flattenRoutes = (routes: RouteModel[]) => {
  const flattened: RouteModel[] = [];

  routes.forEach((route) => {
    if (route.children && route.children.length > 0) {
      flattened.push(...flattenRoutes(route.children));
    }

    flattened.push(route);
  });

  return flattened;
};

interface WrapperProps {
  newTheme: boolean;
  withoutContent: boolean;
  children: ReactNode;
}

const Wrapper = ({
  newTheme,
  withoutContent = false,
  children,
}: WrapperProps) =>
  newTheme ? (
    <FullWidthScopedCssBaseline>{children}</FullWidthScopedCssBaseline>
  ) : withoutContent ? (
    <OldTheme>{children}</OldTheme>
  ) : (
    <Content>
      <OldTheme>{children}</OldTheme>
    </Content>
  );

const FullWidthScopedCssBaseline = styled(ScopedCssBaseline)({
  width: "inherit",
});

interface AbilityComponentProps {
  can: { do: Action; on: Subject; field?: string } | null;
  Component: RouteModel["component"];
  props: unknown;
}

function AbilityComponent({ can, Component, props }: AbilityComponentProps) {
  if (!Component) {
    return <Suspense fallback={<Loader />} />;
  }

  if (!can) {
    return (
      <Suspense fallback={<Loader />}>
        <Component {...props} />
      </Suspense>
    );
  }

  return (
    <Suspense fallback={<Loader />}>
      <Can do={can.do} on={can.on} field={can.field}>
        <Component {...props} />
      </Can>
      <Can do={can.do} on={can.on} field={can.field} not>
        <Page403 />
      </Can>
    </Suspense>
  );
}

export const useBuildChildRoutes = (
  routes: RouteModel[],
  withoutContent: boolean = false,
  ability?: AppAbilityType
) => {
  return useMemo(
    () =>
      flattenRoutes(routes)
        .filter(({ path }) => path)
        .map((route) => {
          if (
            route.children?.length &&
            route.showChildrenInSidebar &&
            ability
          ) {
            const firstPossibleToViewRoute =
              route.children.find(
                ({ can }) => !can || (can && ability.can(can.do, can.on))
              ) ?? route.children[0];

            return {
              ...route,
              component: () => <Redirect to={firstPossibleToViewRoute.path} />,
            };
          }

          return route;
        })
        .map(
          ({
            path,
            can = null,
            newTheme = false,
            hasNestedSwitch = false,
            component: Component,
          }) => (
            <SentryRoute
              key={path}
              path={path}
              exact={!hasNestedSwitch}
              render={(props: unknown) => (
                <Wrapper newTheme={newTheme} withoutContent={withoutContent}>
                  <AbilityComponent
                    can={can}
                    props={props}
                    Component={Component}
                  />
                </Wrapper>
              )}
            />
          )
        ),
    [ability, routes, withoutContent]
  );
};

export function useAppRoutes(
  appRoutesFilter?: (routes: RouteModel[]) => RouteModel[]
) {
  const ability = useAbility();
  const switchAuthRoutes = useBuildChildRoutes(authRoutes, true);
  const switchAppRoutes = useBuildChildRoutes(appRoutes, false, ability);

  const filteredValues = useMemo(() => {
    const isAppRoutesFilterProvided = typeof appRoutesFilter === "function";

    if (isAppRoutesFilterProvided) {
      const filteredAppRoutes = appRoutesFilter(appRoutes);
      const filteredAppPaths = filteredAppRoutes.map((route) => route.path);

      return {
        filteredAppRoutes,
        filteredAppPaths,
      };
    }

    return {};
  }, [appRoutesFilter]);

  const authPaths = authRoutes.map((route) => route.path);
  const appPaths = appRoutes.map((route) => route.path);

  const allRoutes = [...switchAppRoutes, ...switchAuthRoutes].map((route) =>
    String(route.key)
  );

  return {
    switchAuthRoutes,
    switchAppRoutes,
    authPaths,
    appPaths,
    allRoutes,
    ...filteredValues,
  };
}
